import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { globe_02, users_01 } from "@tql/ui-components-react/untitled/icons";

import { LocalDateTextFilterCell } from "~/shared/components/trax-grid/date-text-filter-cell/date-text-filter-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import type { RoutingGuideType } from "~/shared/schemas/routing-guide-schema";
import { formatDateToMMDDYYYY } from "~/shared/utils/string-fns.ts";

import { RoutingGuideActionsCell } from "../cells/action-cell/index.tsx";
import { RoutingGuideActiveCell } from "../cells/active-cell/index.tsx";
import { RoutingGuideAutoSelectCell } from "../cells/auto-select-cell/index.tsx";

export type RoutingGuideGridItem = RoutingGuideType & {
	details: string;
	level: string;
};

type RoutingGuideDataCellField = "name" | "details" | "level" | "createdByName";

const renderDataCell = (field: RoutingGuideDataCellField, testId: string) => {
	function DataCell(props: Readonly<GridCustomCellProps>) {
		const dataItem = props.dataItem as RoutingGuideGridItem;

		return (
			<GridTd data-testid={testId} tdProps={props.tdProps}>
				{dataItem[field]}
			</GridTd>
		);
	}

	return DataCell;
};

export const getRoutingGuideColumns = (
	onHandleOpenEditModal: (guide: RoutingGuideType) => void,
	handleDelete: (id: number) => void,
	canEdit: boolean,
	isCustomerLayout = false,
	activeCustomerId?: number,
) =>
	[
		{
			field: "name",
			title: "Rule Set Name",
			menu: true,
			show: true,
			width: 200,
			cells: {
				data: renderDataCell("name", "routing-guide-cell-rule-set-name"),
			},
		},
		{
			field: "details",
			title: "Quote/Shipment Details",
			menu: true,
			show: true,
			sortable: false,
			cells: {
				data: renderDataCell(
					"details",
					"routing-guide-cell-quote-shipment-details",
				),
			},
		},
		{
			field: "level",
			title: "Carrier/Service Level",
			menu: true,
			show: true,
			sortable: false,
			cells: {
				data: renderDataCell(
					"level",
					"routing-guide-cell-carrier-service-level",
				),
			},
		},
		{
			field: "createdByName",
			title: "Created By",
			editable: false,
			menu: true,
			show: true,
			width: 150,
			cells: {
				data: renderDataCell("createdByName", "routing-guide-cell-created-by"),
			},
		},
		{
			field: "createdDate",
			title: "Created Date",
			editable: false,
			menu: true,
			show: true,
			width: 150,
			filterCell: LocalDateTextFilterCell,
			cells: {
				data: (props: GridCustomCellProps) => (
					<GridTd
						data-testid={"routing-guide-cell-created-date"}
						tdProps={props.tdProps}
					>
						{formatDateToMMDDYYYY(
							(props.dataItem as RoutingGuideGridItem).createdDate,
						)}
					</GridTd>
				),
			},
		},
		{
			field: "isGlobal",
			title: "Global / CID",
			menu: false,
			filterable: false,
			sortable: false,
			show: true,
			width: 70,
			cells: {
				data: (props: GridCustomCellProps) => (
					<GridTd
						data-testid={"routing-guide-cell-global-cid"}
						tdProps={props.tdProps}
					>
						<SvgIcon
							icon={
								(props.dataItem as RoutingGuideType).customerId
									? users_01
									: globe_02
							}
							size={"xlarge"}
						/>
					</GridTd>
				),
			},
		},
		{
			field: "isActive",
			title: "Active",
			menu: false,
			filterable: false,
			sortable: false,
			show: true,
			width: 70,
			cells: {
				data: (props: GridCustomCellProps) => (
					<RoutingGuideActiveCell
						testId={"routing-guide-cell-active"}
						{...props}
						canEdit={canEdit}
						isCustomerLayout={isCustomerLayout}
					/>
				),
			},
		},
		{
			field: "isAutoSelect",
			title: "Auto Select",
			menu: false,
			filterable: false,
			sortable: false,
			show: true,
			width: 220,
			cells: {
				data: (props: GridCustomCellProps) => (
					<RoutingGuideAutoSelectCell
						testId={"routing-guide-cell-auto-select"}
						{...props}
					/>
				),
			},
		},
		canEdit
			? {
					field: "",
					title: "Actions",
					menu: true,
					filterable: false,
					sortable: false,
					width: 70,
					show: true,
					cells: {
						data: (props: GridCustomCellProps) => {
							return (
								<RoutingGuideActionsCell
									testId={"routing-guide-cell-actions"}
									{...props}
									activeCustomerId={activeCustomerId}
									isCustomerLayout={isCustomerLayout}
									onHandleDelete={handleDelete}
									onHandleOpenEditModal={onHandleOpenEditModal}
								/>
							);
						},
					},
				}
			: null,
	].filter((col): col is NonNullable<typeof col> => col !== null);
